import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, GraduationCap } from 'lucide-react';
import useAppStore from '../../../../store/appStore';
import {
  buildAutoplayEmbedUrl,
  fetchVimeoPosterUrl,
  getEmbedUrl,
  getVimeoPosterUrl,
  getYoutubePosterUrl,
  isVideoEmbedUrl,
  normalizeEmbedSrc,
} from '../../../../utils/videoEmbed';
import {
  AcademyModuleData,
  PE_FACTS_ACADEMY_SEMINAR_FALLBACK_SRC,
  PE_FACTS_ACADEMY_SEMINAR_TITLE,
  PE_FACTS_ACADEMY_SEMINAR_VIDEO_ID,
  PE_FACTS_VIDEO_SRC,
} from '../types';

const ACADEMY_MODULES: AcademyModuleData[] = [
  {
    id: 'mindset-gewinnpflicht',
    number: '01',
    badge: 'Mindset & Recht',
    title: 'Keine 08/15-Anleitung: Es geht ums nackte Überleben',
    subtitle: 'Die Kopfbarriere abbauen & die gesetzliche Pflicht zum Gewinn erfüllen',
    coreQuote:
      'Gewinn ist kein nettes Extra oder eine Option – es ist die oberste gesetzliche Pflicht als Unternehmer.',
    takeaways: [
      'Preisanpassungen scheitern extrem selten am Kunden. Sie scheitern fast immer an der eigenen Angst im Kopf und an unsauberer Kalkulation.',
      'Finanzamt & Betriebsprüfung interessieren sich nicht für Leidenschaft oder Handwerkskunst, sondern fordern den juristischen Nachweis einer echten Gewinnabsicht.',
      'Die Liebhaberei-Falle: Wer über Jahre nur eine schwarze Null ausweist, wird vom Prüfer nicht bemitleidet. Fehlender Gewinn wird als verdeckte Einnahme geschätzt – existenzvernichtende Nachzahlungen drohen.',
    ],
    actionStep:
      'Verstehe deinen Salon nicht als Hobbybetrieb: Wer wirtschaftlich überleben will, muss die Preise kaufmännisch fundiert durchsetzen.',
  },
  {
    id: 'boiler-prinzip',
    number: '02',
    badge: 'Kalkulation & Marge',
    title: 'Der Schock-Vergleich: Das Boiler-Prinzip',
    subtitle: 'Warum Handwerker reich werden und Friseure an biologische Grenzen stoßen',
    coreQuote:
      'Der Installateur wird nicht durch seinen Stundensatz reich – er verdient sein Geld am Boiler. Wir Friseure verkaufen bisher fast nur nackte Zeit.',
    takeaways: [
      '3 Stunden Installateur: Er baut für 1.800 € einen Kessel ein und erzielt mit Materialspanne, Anfahrt und Montage rund 800 € Reingewinn.',
      '3 Stunden Friseur: Höchste Handwerkskunst für 300 € Balayage bringt nach Personal-, Raum- und Fixkosten bei 10 % Rendite oft nur 25 bis 30 € Reingewinn.',
      'Das biologische Limit: Wer ausschließlich die Arbeitsminuten seiner Hände abrechnet, stößt an ein starres Einkommenslimit. Mehr als arbeiten geht nicht.',
      'Friseur-Ersatzteile: Wir brauchen Dienstleistungen mit echter Materialmarge (Extensions, strukturierte Veredelungen) und konsequenten Home-Care-Verkauf (Retail).',
    ],
    actionStep:
      'Kopple Dienstleistung und Materialverkauf: Schon eine Verschiebung von 15 % deines Mixes hin zu Dienstleistung + Verkauf verdoppelt das Betriebsergebnis.',
  },
  {
    id: 'gesetzesfalle-2026',
    number: '03',
    badge: 'Compliance & Schutz',
    title: 'Die Gesetzesfalle ab 2026: Risikobranche Friseur',
    subtitle: 'Schwarzarbeitsbekämpfung, Sofortmeldepflicht und bis zu 100.000 € Bußgeld',
    coreQuote:
      'Wenn deine offiziellen Preise deinen Betrieb wirtschaftlich nicht tragen können, unterstellt der Zoll Schwarzgeld.',
    takeaways: [
      'Gesetzliche Einstufung als offizielle Risikobranche: Verschärfte Pflichten bei Ausweismitführung, lückenloser Arbeitszeiterfassung und Sofortmeldungen.',
      'Horrende Bußgelder von bis zu 100.000 € brechen unsauber kalkulierten Betrieben sofort das Genick.',
      'Der rechtliche Airbag: Eine schriftliche Vollkostenkalkulation ist dein juristisches Schutzschild bei Betriebsprüfungen durch Zoll, Finanzamt und Rentenversicherung.',
    ],
    actionStep:
      'Hinterlege deine Stundensätze und Minutenpreise transparent im System, um die kaufmännische Notwendigkeit jederzeit beweisen zu können.',
  },
  {
    id: '1-zu-4-formel',
    number: '04',
    badge: 'Preiselastizität',
    title: 'Die mathematische Schmerzgrenze & Die 1:4-Praxisformel',
    subtitle: 'Warum 20 % Preisanpassung kein unternehmerisches Risiko bedeuten',
    coreQuote:
      'Wer wegen 5 bis 10 Euro geht, war nie dein Stammkunde – sondern Stammkunde deines zu billigen Preises.',
    takeaways: [
      'Die Null-Risiko-Zone: Verlangst du 20 % mehr, können rechnerisch bis zu 16,7 % bis 20 % der Kunden wegbleiben, ohne dass du auch nur einen einzigen Cent Umsatz verlierst.',
      'Die empirische 1:4-Praxisformel: In der Praxis folgt auf 4 % Preisanpassung im Schnitt maximal 1 % Kundenschwund (20 % Anpassung bedeutet ca. 4,75 % bis 5 % Schwund).',
      'Zielgruppen-Wandlung: Billig zieht Billig an. Qualitätsbewusste Kunden erwarten faire Preise, kaufen Pflegeprodukte und sind deutlich zufriedener zu bedienen.',
      'Live-Fallstudie: 27.133 € Monatsumsatz, 19 % Anpassung, 4,75 % Schwund -> Umsatz klettert auf 30.755 € netto. Das Betriebsergebnis explodiert von 442 € auf 4.064 € (+819 %!).',
    ],
    actionStep:
      'Nutze die freiwerdende Zeit bei moderatem Kundenschwund gezielt für intensivere Beratung und Zusatzverkäufe.',
  },
  {
    id: 'ertragshebel-auslastung',
    number: '05',
    badge: 'Controlling & Hebel',
    title: 'Die 3 Ertragshebel & Die Auslastungs-Falle (>75 %)',
    subtitle: 'Warum ein randvoller Salon kein Grund zum Feiern ist, sondern krank macht',
    coreQuote:
      'Jede Auslastung über 75 % ist nachweislich krankmachende Akkordarbeit und vernichtet Zeit für lukrative Zusatzverkäufe.',
    takeaways: [
      'Hebel 1 (Controlling): Wareneinsatz und unnötige Fixkosten dämpfen. Wer seine BWA erst 8 Wochen später sieht, fährt blind über die Autobahn.',
      'Hebel 2 (Kostendeckung): Jeder Stuhl und jede Salonminute muss monatlich seine Fixkosten einspielen, bevor der erste Euro Gewinn entsteht.',
      'Die Auslastungs-Falle (>75 %): Dauerstress blockiert Ruhe für Kopfhautberatung, Glossing-Upsells und Heimpflege-Empfehlungen. Kunden spüren die Hetze.',
    ],
    actionStep:
      'Reduziere den Termindruck bewusst über saubere Preise, um wieder Raum für erstklassige Beratung zu gewinnen.',
  },
  {
    id: 'qualitaetsfriseur',
    number: '06',
    badge: 'Positionierung',
    title: 'Zukunft: Wer überlebt? Der Weg zum Qualitätsfriseur',
    subtitle: 'Die goldene Mitte als einzig nachhaltiges Geschäftsmodell der Branche',
    coreQuote:
      'Werde vom reinen Dienstleister zum Erlebnis-Anbieter – wenn du nur Haare schneidest, bist du vergleichbar und angreifbar.',
    takeaways: [
      'Die Marktspaltung: Billig-Ketten und unsaubere Betriebe werden durch steigende Mindestlöhne und Kontrolldruck verdrängt.',
      'Die goldene Mitte: Wertige Salons mit gehobener Kundschaft sichern dauerhaft solide Renditen.',
      'Sichtbare Expertise: Fundierte Beratung, strukturierte Kopfhautanalysen und personalisierte Pflegepläne rechtfertigen verlässlich höhere Preise.',
    ],
    actionStep:
      'Ersetze pauschale Dienstleistungen durch fundierte Behandlungs- und Beratungsrituale.',
  },
  {
    id: 'team-schutzschild',
    number: '07',
    badge: 'Team & Umsetzung',
    title: 'Das Team-Mindset & Das Bodyguard-Prinzip',
    subtitle: 'Schluss mit verdecktem Unterkassieren und Preisdiskussionen am Bedienplatz',
    coreQuote: 'Preisanpassungen werden NIEMALS am Stuhl oder am Waschbecken diskutiert.',
    takeaways: [
      'Das verdeckte Unterkassieren an der Kasse: Stylisten buchen aus Mitleid oder Scheu günstigere Positionen ein oder lassen Pflegekuren unberechnet. Das ist kein Service, sondern offener Ertragsdiebstahl.',
      'Perspektivenwechsel im Team: Sauberes Erfassen sichert faire Löhne, modernste Arbeitsmittel und das Überleben des Salons.',
      'Der Aufsteller als Bodyguard: Keine Vorwarn-Briefe oder Entschuldigungen am Telefon. Der Aufsteller am Platz ist die gesetzte Norm: „Entscheidung der Geschäftsführung. Ich bin heute hier, um Sie glücklich zu machen.“',
    ],
    actionStep:
      'Platziere den Aufsteller im Salon als klares Schutzschild für dein Team vor jeglicher Preisdiskussion.',
  },
];

const SeminarPlayer: React.FC<{ src: string; title: string }> = ({ src, title }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const embedSrc = normalizeEmbedSrc(src);

  useEffect(() => {
    let cancelled = false;
    const yt = getYoutubePosterUrl(embedSrc);
    if (yt) {
      setPosterUrl(yt);
      return;
    }
    if (!isVideoEmbedUrl(embedSrc)) {
      setPosterUrl(null);
      return;
    }
    setPosterUrl(getVimeoPosterUrl(embedSrc));
    fetchVimeoPosterUrl(embedSrc).then((url) => {
      if (!cancelled && url) setPosterUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [embedSrc]);

  if (!embedSrc) {
    return (
      <div className="aspect-video w-full bg-slate-950 flex items-center justify-center text-sm text-slate-400">
        Seminar-Video folgt
      </div>
    );
  }

  if (isVideoEmbedUrl(embedSrc) && !isPlaying) {
    return (
      <button
        type="button"
        onClick={() => setIsPlaying(true)}
        className="relative aspect-video w-full border-0 p-0 cursor-pointer bg-slate-900"
        aria-label={`${title} abspielen`}
      >
        {posterUrl ? (
          <img
            src={posterUrl}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="absolute inset-0 bg-slate-900" />
        )}
        <span className="absolute inset-0 flex items-center justify-center bg-black/35">
          <span className="w-16 h-16 rounded-full bg-[#E6C299] text-black flex items-center justify-center shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 ml-0.5">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </span>
      </button>
    );
  }

  return (
    <div className="aspect-video w-full bg-black">
      <iframe
        className="w-full h-full border-0"
        src={isPlaying ? buildAutoplayEmbedUrl(embedSrc) : getEmbedUrl(embedSrc)}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
      />
    </div>
  );
};

export const PeFactsAcademy: React.FC = () => {
  const [openModuleId, setOpenModuleId] = useState<string>('mindset-gewinnpflicht');
  const tutorialVideos = useAppStore((s) => s.settings?.tutorialVideos || []);

  const seminar = useMemo(() => {
    const list = Array.isArray(tutorialVideos) ? tutorialVideos : [];
    const byId = list.find((v: any) => v?.id === PE_FACTS_ACADEMY_SEMINAR_VIDEO_ID);
    if (byId?.src) return byId;
    const byTitle = list.find((v: any) =>
      String(v?.title || '')
        .toLowerCase()
        .includes('preisanpassung')
    );
    if (byTitle?.src) return byTitle;
    return {
      id: PE_FACTS_ACADEMY_SEMINAR_VIDEO_ID,
      title: PE_FACTS_ACADEMY_SEMINAR_TITLE,
      description:
        'Preisanpassung – Strategie, Mindset & nacktes Überleben für Saloninhaber. Schluss mit Bauchgefühl und Preisangst!',
      src: PE_FACTS_ACADEMY_SEMINAR_FALLBACK_SRC,
    };
  }, [tutorialVideos]);

  const toggleModule = (id: string) => {
    setOpenModuleId((prev) => (prev === id ? '' : id));
  };

  return (
    <div className="space-y-8 text-slate-100 p-6 md:p-8">
      {/* Kurz-Video Intro */}
      <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 shadow-2xl">
        <div className="aspect-video w-full bg-slate-900">
          <video
            controls
            playsInline
            className="w-full h-full object-cover"
            src={PE_FACTS_VIDEO_SRC}
          >
            Ihr Browser unterstützt die Videowiedergabe nicht.
          </video>
        </div>
        <div className="p-4 bg-slate-900/90 border-t border-slate-800 flex items-center justify-between gap-3">
          <div>
            <span className="text-xs uppercase tracking-widest text-[#E6C299] font-bold">
              Kurz-Intro
            </span>
            <h4 className="text-base font-semibold text-slate-100">
              Strategie, Mindset & nacktes Überleben
            </h4>
          </div>
          <span className="text-xs px-2.5 py-1 rounded-full bg-slate-800 text-slate-300 border border-slate-700 shrink-0">
            kompakt
          </span>
        </div>
      </div>

      {/* Accordion-Module – lesbare Schriftgröße */}
      <div className="space-y-3">
        {ACADEMY_MODULES.map((module) => {
          const isOpen = openModuleId === module.id;

          return (
            <div
              key={module.id}
              className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                isOpen
                  ? 'bg-slate-900/90 border-[#E6C299]/40 shadow-lg'
                  : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
              }`}
            >
              <button
                type="button"
                onClick={() => toggleModule(module.id)}
                className="w-full p-4 md:p-5 text-left flex items-center justify-between gap-4 select-none"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-sm font-mono font-bold px-2.5 py-1.5 rounded bg-slate-800 text-[#E6C299] border border-[#E6C299]/20 shrink-0">
                    {module.number}
                  </span>
                  <div className="min-w-0">
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 font-bold block">
                      {module.badge}
                    </span>
                    <h3 className="text-base md:text-lg font-bold text-slate-100 leading-snug">
                      {module.title}
                    </h3>
                  </div>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-slate-400 transition-transform duration-200 shrink-0 ${
                    isOpen ? 'rotate-180 text-[#E6C299]' : ''
                  }`}
                />
              </button>

              {isOpen && (
                <div className="px-4 md:px-5 pb-5 pt-2 space-y-4 text-sm md:text-base border-t border-slate-800/60">
                  <p className="text-slate-300 italic text-sm md:text-base leading-relaxed">
                    {module.subtitle}
                  </p>

                  <div className="p-4 rounded-lg bg-slate-950/60 border-l-2 border-[#E6C299] text-slate-100 font-medium text-sm md:text-base leading-relaxed">
                    „{module.coreQuote}“
                  </div>

                  <div className="space-y-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#E6C299] block">
                      Kernfakten:
                    </span>
                    <ul className="space-y-2.5 text-slate-200 text-sm md:text-base leading-relaxed">
                      {module.takeaways.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2.5">
                          <span className="text-[#E6C299] shrink-0 mt-0.5">✦</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-3.5 rounded-lg bg-slate-800/40 border border-slate-700/60 flex items-start gap-2.5 text-slate-100 text-sm md:text-base leading-relaxed">
                    <CheckCircle2 className="w-5 h-5 text-[#E6C299] shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-slate-100 block text-xs uppercase tracking-wider mb-1">
                        Umsetzungs-Fokus:
                      </strong>
                      {module.actionStep}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Volles Academy-Seminar (auch wenn in Academy noch nicht öffentlich) */}
      <div className="rounded-2xl border border-[#E6C299]/30 bg-slate-900/80 overflow-hidden shadow-2xl">
        <div className="p-5 md:p-6 border-b border-slate-800 space-y-2">
          <div className="flex items-center gap-2 text-[#E6C299]">
            <GraduationCap className="w-5 h-5" />
            <span className="text-xs font-black uppercase tracking-widest">
              Academy-Seminar
            </span>
          </div>
          <h3 className="text-lg md:text-xl font-black text-white leading-snug">
            {seminar.title || PE_FACTS_ACADEMY_SEMINAR_TITLE}
          </h3>
          <p className="text-sm md:text-base text-slate-300 leading-relaxed">
            {seminar.description ||
              'Das vollständige Preisanpassungs-Seminar aus der PreisExpert Academy – hier direkt in PEFacts für freigeschaltete Mandanten.'}
          </p>
        </div>
        <SeminarPlayer
          src={seminar.src || PE_FACTS_ACADEMY_SEMINAR_FALLBACK_SRC}
          title={seminar.title || PE_FACTS_ACADEMY_SEMINAR_TITLE}
        />
      </div>
    </div>
  );
};

export default PeFactsAcademy;
